import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  Suspense,
} from "react";
import { useLocation } from "react-router-dom";
import type {
  PopupQueueItem,
  PopupQueueContextValue,
} from "./PopupQueueContext.types";
import { getGlobalState } from "../../stores/GlobalState";
import { ROUTES } from "../../types/routes";
import { POPUP_CONFIGS } from "../../config/popupConfig";

const PopupQueueContext = createContext<PopupQueueContextValue | undefined>(
  undefined
);

/**
 * 在 Provider 的全局 children（含 Event/useQueuedPopup）之后执行 render，
 * 避免父组件 useMemo 早于子树同步 isInQueueRef / popupItemsRef 导致 stillShouldShow 误判、连弹全部被跳过。
 */
const QueuedPopupSuspenseOutlet: React.FC<{
  currentPopup: PopupQueueItem | null;
  completeCurrentAndNext: () => void;
}> = ({ currentPopup, completeCurrentAndNext }) => {
  return useMemo(() => {
    if (!currentPopup) return null;

    try {
      const result = currentPopup.render();
      if (!result) {
        setTimeout(() => {
          completeCurrentAndNext();
        }, 0);
        return null;
      }
      return result;
    } catch {
      setTimeout(() => {
        completeCurrentAndNext();
      }, 0);
      return null;
    }
  }, [currentPopup, completeCurrentAndNext]);
};

let globalIdCounter = 0;

// 检查路径是否允许显示弹窗
const isPathAllowed = (
  currentPath: string,
  allowedPaths?: string[],
  blockedPaths?: string[]
): boolean => {
  // 1. 检查是否在禁止路径列表
  if (blockedPaths && blockedPaths.length > 0) {
    const isBlocked = blockedPaths.some(
      blockedPath =>
        currentPath === blockedPath || currentPath.startsWith(blockedPath + "/")
    );
    if (isBlocked) {
      return false; // 在禁止列表中，不允许
    }
  }

  // 2. 检查是否在允许路径列表
  if (allowedPaths && allowedPaths.length > 0) {
    return allowedPaths.some(
      allowedPath =>
        currentPath === allowedPath || currentPath.startsWith(allowedPath + "/")
    );
  }

  // 3. 如果配置了 blockedPaths 但没有配置 allowedPaths
  if (blockedPaths && blockedPaths.length > 0) {
    return true; // 不在禁止列表中，允许
  }

  // 4. 如果都没有配置，默认只允许在首页
  return (
    currentPath === ROUTES.HOME ||
    currentPath === ROUTES.HOME_ALT ||
    currentPath === "/"
  );
};

export const PopupQueueProvider: React.FC<{
  children: React.ReactNode;
  /** 初始是否暂停队列（用于等待前置弹窗完成后再 resume） */
  initialPaused?: boolean;
}> = ({ children, initialPaused = false }) => {
  const location = useLocation();
  const [queue, setQueue] = useState<PopupQueueItem[]>([]);
  const [currentPopup, setCurrentPopup] = useState<PopupQueueItem | null>(null);
  const [isPaused, setIsPaused] = useState(initialPaused);
  // 使用 ref 来存储 currentPopup
  const currentPopupRef = useRef<PopupQueueItem | null>(null);
  // 使用 ref 来存储 timer，防止路由切换时重置
  const displayTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 存储被暂时隐藏的弹窗（因为路径不匹配）
  const suspendedPopupRef = useRef<PopupQueueItem | null>(null);
  // 控制"每次进入首页只弹一个"
  const hasShownThisHomeVisitRef = useRef<boolean>(false);
  const prevIsHomeRef = useRef<boolean>(false);
  const RECHARGE_PROMOTION_ID = POPUP_CONFIGS.RECHARGE_PROMOTION.id;

  // ACTIVITY_QUALIFICATION 类型弹窗限制
  const ACTIVITY_QUALIFICATION_TYPE = "ACTIVITY_QUALIFICATION";
  const ACTIVITY_QUALIFICATION_MAX_IN_QUEUE = 2;
  const activityQualificationCountRef = useRef<number>(0);

  // 添加初始化延迟标记，等待异步用户数据加载完成
  const [isInitialDelayDone, setIsInitialDelayDone] = useState(false);
  const INITIAL_DELAY_MS = 500; // 延迟 500ms 等待用户数据加载

  // 初始化延迟，等待用户数据异步加载完成
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialDelayDone(true);
    }, INITIAL_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  // 同步 currentPopup 到 ref
  useEffect(() => {
    currentPopupRef.current = currentPopup;
  }, [currentPopup]);

  // 仅开发环境输出队列中的弹窗，便于排查弹窗顺序问题
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const queuedPopupIds = queue.map(item => item.popupId ?? item.id);
    const currentPopupId = currentPopup?.popupId ?? currentPopup?.id ?? null;

    console.log("[PopupQueue] 在队列中的弹窗:", queuedPopupIds);
    console.log("[PopupQueue] 当前显示弹窗:", currentPopupId);
  }, [queue, currentPopup]);

  // 添加弹窗到队列
  const enqueue = useCallback((item: Omit<PopupQueueItem, "id">) => {
    const id = `popup-${++globalIdCounter}`;
    const newItem: PopupQueueItem = { ...item, id };
    let shouldEnqueue = true;

    setQueue(prev => {
      // 如果提供了 popupId，检查队列中是否已存在相同的弹窗
      if (newItem.popupId) {
        const existsInQueue = prev.some(p => p.popupId === newItem.popupId);
        if (existsInQueue) {
          // 已存在，不重复加入
          console.log(
            `[PopupQueue-Enqueue] ${newItem.popupId} 已在队列中，拒绝加入`
          );
          shouldEnqueue = false;
          return prev;
        }
        // 检查当前正在显示的弹窗
        if (currentPopupRef.current?.popupId === newItem.popupId) {
          // 正在显示，不重复加入
          console.log(
            `[PopupQueue-Enqueue] ${newItem.popupId} 正在显示，拒绝加入`
          );
          shouldEnqueue = false;
          return prev;
        }
      }

      let updatedQueue = [...prev];

      // 如果新加入的是 ACTIVITY_QUALIFICATION 类型
      // 如果是 ACTIVITY_QUALIFICATION 类型且不是手动触发，检查队列中是否已有2个
      if (
        newItem.timingType === ACTIVITY_QUALIFICATION_TYPE &&
        !newItem.isManualTrigger
      ) {
        // 统计队列中已有的 ACTIVITY_QUALIFICATION 类型弹窗
        const activityQualificationItems = updatedQueue.filter(
          p => p.timingType === ACTIVITY_QUALIFICATION_TYPE
        );

        // 如果已经有2个或更多，需要移除优先级最低的
        if (
          activityQualificationItems.length >=
          ACTIVITY_QUALIFICATION_MAX_IN_QUEUE
        ) {
          // 找出优先级最低的（数字最大的）ACTIVITY_QUALIFICATION 弹窗
          const lowestPriorityItem = activityQualificationItems.reduce(
            (lowest, current) => {
              return current.priority > lowest.priority ? current : lowest;
            }
          );

          // 如果新弹窗的优先级比最低优先级还低，直接不加入
          if (newItem.priority >= lowestPriorityItem.priority) {
            // 延迟调用 onRejected，避免在 setState 中触发其他状态更新
            setTimeout(() => {
              newItem.onRejected?.();
            }, 0);
            shouldEnqueue = false;
            return prev;
          }

          updatedQueue = updatedQueue.filter(
            p => p.id !== lowestPriorityItem.id
          );

          setTimeout(() => {
            lowestPriorityItem.onRejected?.();
          }, 0);
        }
      }

      // 加入新弹窗并按优先级排序（数字越小优先级越高）
      const newQueue = [...updatedQueue, newItem];
      const sorted = newQueue.sort((a, b) => a.priority - b.priority);
      console.log(
        `[PopupQueue-Enqueue] ${newItem.popupId} 成功加入队列，ID: ${id}`
      );
      return sorted;
    });

    // 根据实际是否加入队列来返回 ID 或 null
    return shouldEnqueue ? id : null;
  }, []);

  // 移除指定弹窗
  const dequeue = useCallback(
    (id: string) => {
      setQueue(prev => prev.filter(item => item.id !== id));

      if (currentPopup?.id === id) {
        setCurrentPopup(null);
      }
    },
    [currentPopup]
  );

  // 完成当前弹窗，显示下一个
  const completeCurrentAndNext = useCallback(() => {
    const popup = currentPopupRef.current;
    if (popup) {
      // 调用完成回调
      popup.onComplete?.();
      setCurrentPopup(null);
    }
  }, []);

  // 暂停队列
  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  // 恢复队列
  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  // 尝试显示队列中的下一个弹窗
  const tryShowNextPopup = useCallback(() => {
    // 如果队列已暂停，不显示新弹窗
    if (isPaused) {
      return;
    }

    // 如果初始化延迟还没完成，等待
    if (!isInitialDelayDone) {
      return;
    }

    // 如果已经有定时器在运行，不重复创建
    if (displayTimerRef.current) {
      return;
    }

    // 检查是否正在重定向
    const globalState = getGlobalState();
    if (globalState.isRedirecting) {
      return;
    }

    // 如果没有弹窗在显示且队列中有弹窗
    if (!currentPopup && queue.length > 0) {
      displayTimerRef.current = setTimeout(() => {
        displayTimerRef.current = null;

        // 再次检查是否正在重定向
        const currentGlobalState = getGlobalState();
        if (currentGlobalState.isRedirecting) {
          return;
        }

        // 获取队列中的第一个弹窗（已经按优先级排序）
        const nextPopup = queue[0];
        if (nextPopup) {
          // 检查是否为 ACTIVITY_QUALIFICATION 类型且不是手动触发且已达到显示上限
          if (
            nextPopup.timingType === ACTIVITY_QUALIFICATION_TYPE &&
            !nextPopup.isManualTrigger &&
            activityQualificationCountRef.current >=
              ACTIVITY_QUALIFICATION_MAX_IN_QUEUE
          ) {
            setQueue(prev => prev.filter(p => p.id !== nextPopup.id));
            // 调用被移除弹窗的 onRejected 回调（不是 onComplete）
            nextPopup.onRejected?.();
            return;
          }

          // 检查当前路径是否允许显示这个弹窗
          const currentPath = window.location.pathname;
          if (
            isPathAllowed(
              currentPath,
              nextPopup.allowedPaths,
              nextPopup.blockedPaths
            )
          ) {
            // 如果是 ACTIVITY_QUALIFICATION 类型且不是手动触发，增加计数
            if (
              nextPopup.timingType === ACTIVITY_QUALIFICATION_TYPE &&
              !nextPopup.isManualTrigger
            ) {
              activityQualificationCountRef.current += 1;
            }

            setCurrentPopup(nextPopup);
            setQueue(prev => prev.slice(1));
          }
          // 如果不允许显示，不从队列中移除，等待路径变化后再检查
        }
      }, 100);
    }
  }, [currentPopup, queue, isInitialDelayDone, isPaused]);

  // 监听路径变化，控制弹窗的显示和隐藏
  useEffect(() => {
    const currentPath = location.pathname;
    const isHome =
      currentPath === ROUTES.HOME ||
      currentPath === ROUTES.HOME_ALT ||
      currentPath === "/";
    // 检测"进入首页"事件：从非首页 -> 首页 时重置"本次进入首页已弹出"标记
    if (isHome && !prevIsHomeRef.current) {
      hasShownThisHomeVisitRef.current = false;
      // 重置 ACTIVITY_QUALIFICATION 类型弹窗计数
      activityQualificationCountRef.current = 0;

      // 清理队列中多余的 ACTIVITY_QUALIFICATION 类型弹窗，只保留优先级最高的2个
      setQueue(prev => {
        const activityQualificationItems = prev.filter(
          p => p.timingType === ACTIVITY_QUALIFICATION_TYPE
        );
        const otherItems = prev.filter(
          p => p.timingType !== ACTIVITY_QUALIFICATION_TYPE
        );

        if (
          activityQualificationItems.length >
          ACTIVITY_QUALIFICATION_MAX_IN_QUEUE
        ) {
          // 按优先级排序，保留优先级最高的2个
          const sortedActivityItems = [...activityQualificationItems].sort(
            (a, b) => a.priority - b.priority
          );
          const toKeep = sortedActivityItems.slice(
            0,
            ACTIVITY_QUALIFICATION_MAX_IN_QUEUE
          );
          const toRemove = sortedActivityItems.slice(
            ACTIVITY_QUALIFICATION_MAX_IN_QUEUE
          );

          // 调用被移除弹窗的 onRejected 回调（不是 onComplete）
          toRemove.forEach(item => {
            item.onRejected?.();
          });

          // 合并保留的弹窗并重新排序
          const newQueue = [...toKeep, ...otherItems];
          return newQueue.sort((a, b) => a.priority - b.priority);
        }

        return prev;
      });

      try {
        sessionStorage.setItem("home_visit_shown", "0");
      } catch {
        // ignore
      }
    }
    prevIsHomeRef.current = isHome;

    // 如果有弹窗正在显示
    if (currentPopup) {
      // 检查当前路径是否允许显示这个弹窗
      const allowed = isPathAllowed(
        currentPath,
        currentPopup.allowedPaths,
        currentPopup.blockedPaths
      );

      if (!allowed) {
        // 不允许显示，挂起当前弹窗（放回队列头部，并记录）
        suspendedPopupRef.current = currentPopup;
        setQueue(prev => {
          // 确保不重复添加
          const exists = prev.some(item => item.id === currentPopup.id);
          if (exists) return prev;
          return [currentPopup, ...prev];
        });
        setCurrentPopup(null);
      }
    }
    // 如果没有弹窗在显示
    else {
      // 检查队列中是否有可以显示的弹窗
      const nextPopup = queue[0];
      if (nextPopup) {
        const allowed = isPathAllowed(
          currentPath,
          nextPopup.allowedPaths,
          nextPopup.blockedPaths
        );

        if (allowed && !isPaused) {
          // 仅对“充值促销”应用“本次进入首页只弹一个”的限制
          if (nextPopup.popupId === RECHARGE_PROMOTION_ID) {
            const alreadyShownFlag =
              isHome &&
              (hasShownThisHomeVisitRef.current ||
                (() => {
                  try {
                    return sessionStorage.getItem("home_visit_shown") === "1";
                  } catch {
                    return false;
                  }
                })());
            if (alreadyShownFlag) {
              return;
            }
          }
          // 可以显示，尝试显示下一个弹窗
          suspendedPopupRef.current = null;
          // 标记本次进入首页已弹出一个（仅限"充值促销"）
          if (isHome && nextPopup.popupId === RECHARGE_PROMOTION_ID) {
            hasShownThisHomeVisitRef.current = true;
            try {
              sessionStorage.setItem("home_visit_shown", "1");
            } catch {
              // ignore
            }
          }
          // 异步调用，避免在渲染期间更新状态
          setTimeout(() => {
            tryShowNextPopup();
          }, 0);
        }
      }
    }
  }, [
    location.pathname,
    currentPopup,
    queue,
    tryShowNextPopup,
    isInitialDelayDone,
    isPaused,
  ]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (displayTimerRef.current) {
        clearTimeout(displayTimerRef.current);
        displayTimerRef.current = null;
      }
    };
  }, []);

  const value: PopupQueueContextValue = {
    enqueue,
    dequeue,
    completeCurrentAndNext,
    currentPopup,
    hasPopups: queue.length > 0 || currentPopup !== null,
    pause,
    resume,
    isPaused,
  };

  return (
    <PopupQueueContext.Provider value={value}>
      {children}
      {/* 在子树之后再调用 currentPopup.render，避免 stillShouldShow 读到未同步的 ref */}
      <Suspense fallback={null}>
        <QueuedPopupSuspenseOutlet
          currentPopup={currentPopup}
          completeCurrentAndNext={completeCurrentAndNext}
        />
      </Suspense>
    </PopupQueueContext.Provider>
  );
};

export const usePopupQueue = (): PopupQueueContextValue => {
  const context = useContext(PopupQueueContext);
  if (!context) {
    throw new Error("usePopupQueue must be used within PopupQueueProvider");
  }
  return context;
};
